import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Button } from './button';

/**
 * Dialog/Prompt component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 216:2655):
 * - Border Radius: 16px
 * - Padding: 32px
 * - Shadow: elevation with 1px border
 * - Variants: Success, Warning, Error, Info
 * - Icon gradients for each type
 */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva(
  'fixed left-[50%] top-[50%] z-50 grid w-full max-w-[340px] translate-x-[-50%] translate-y-[-50%] gap-6 p-8 shadow-[0px_4px_8px_0px_rgba(11,19,36,0.1),0px_0px_0px_1px_rgba(11,19,36,0.07)] duration-200 rounded-2xl',
  {
    variants: {
      variant: {
        default: 'bg-white dark:bg-grey-900',
        success: 'bg-gradient-to-b from-green-50 to-white dark:from-green-950 dark:to-grey-900',
        warning: 'bg-gradient-to-b from-orange-50 to-white dark:from-orange-950 dark:to-grey-900',
        error: 'bg-gradient-to-b from-red-50 to-white dark:from-red-950 dark:to-grey-900',
        info: 'bg-white dark:bg-grey-900',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const DialogContent = React.forwardRef(({ className, children, variant, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        dialogContentVariants({ variant }),
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn('flex flex-col gap-1 text-center', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn('flex gap-3', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-sm font-medium text-grey-950 dark:text-grey-50', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-black/60 dark:text-white/60', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/**
 * Alert icon with Hyperkit gradient styling
 */
const alertIconStyles = {
  success: 'from-green-400 to-green-700',
  warning: 'from-orange-300 to-orange-600',
  error: 'from-red-400 to-red-700',
  info: 'from-grey-400 to-grey-600',
};

const alertIcons = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

const AlertIcon = ({ variant = 'info' }) => {
  const Icon = alertIcons[variant];
  return (
    <div className={cn(
      'w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-b shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.1),inset_0px_-2px_4px_0px_rgba(255,255,255,0.5)]',
      alertIconStyles[variant]
    )}>
      <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
    </div>
  );
};

/**
 * Prompt - Pre-built dialog for confirmations and alerts
 *
 * Usage:
 *   <Prompt
 *     variant="error"
 *     title="Delete item?"
 *     description="This action cannot be undone."
 *     primaryAction={{ label: 'Delete', onClick: handleDelete }}
 *     secondaryAction={{ label: 'Cancel' }}
 *   />
 */
const Prompt = ({
  open,
  onOpenChange,
  variant = 'info',
  title,
  description,
  primaryAction,
  secondaryAction,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent variant={variant} className="text-center">
      {(variant === 'error' || variant === 'warning') && (
        <div className="flex justify-center">
          <AlertIcon variant={variant} />
        </div>
      )}

      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      {(variant === 'success' || variant === 'info') && (
        <DialogFooter>
          {secondaryAction && (
            <Button variant="secondary" className="flex-1" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          <Button
            variant="default"
            className="flex-1"
            onClick={primaryAction?.onClick}
          >
            {primaryAction?.label || 'OK'}
          </Button>
        </DialogFooter>
      )}

      {(variant === 'error' || variant === 'warning') && (
        <>
          <DialogFooter>
            {secondaryAction && (
              <Button variant="secondary" className="flex-1" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            )}
            <Button
              variant={variant === 'error' ? 'destructive' : 'default'}
              className="flex-1"
              onClick={primaryAction?.onClick}
            >
              {primaryAction?.label || 'OK'}
            </Button>
          </DialogFooter>
          <div className="flex justify-center">
            <AlertIcon variant={variant} />
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
Prompt.displayName = 'Prompt';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Prompt,
  AlertIcon,
  dialogContentVariants,
};
